import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import {
  ArrowTrendingUpIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  ClockIcon,
  DocumentTextIcon,
  EllipsisVerticalIcon,
  FunnelIcon,
  InboxStackIcon,
  Squares2X2Icon,
  MapPinIcon
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import { useWorkdaySummary } from 'hooks/useWorkdaySummary';
import SectionIntro from 'components/common/SectionIntro';

const getCount = value => Number.parseInt(value || 0, 10) || 0;

const formatDate = value => {
  const date = DateTime.fromISO(value || '');
  return date.isValid ? date.toFormat('dd LLL yyyy') : value;
};

const formatTime = (value, fallback) => {
  const date = DateTime.fromISO(value || '');
  return date.isValid ? date.toFormat('HH:mm') : fallback;
};

const SurfaceCard = ({ children, className = '' }) => (
  <section className={`dashboard-surface rounded-md border ${className}`}>{children}</section>
);

const MetricCard = ({ label, value, helper, accent = 'success' }) => (
  <SurfaceCard
    className={`flex h-full flex-col justify-between p-6 ${accent === 'danger' ? 'border-l border-red-300/15' : ''}`}
  >
    <div>
      <p className="font-command-label text-[10px] uppercase tracking-[0.22em] text-slate-400">
        {label}
      </p>
      <h3 className="mt-4 font-command-display text-4xl font-bold text-white">{value}</h3>
    </div>
    <div className="mt-6">
      <div className="command-center-meter-track h-1.5 w-full overflow-hidden rounded-full">
        <div
          className={`h-full rounded-full ${accent === 'danger' ? 'command-center-meter-fill command-center-meter-fill--danger' : 'command-center-meter-fill'}`}
          style={{
            width:
              accent === 'danger'
                ? '15%'
                : `${Math.max(18, Math.min(100, Number.parseFloat(value) || 0))}%`
          }}
        />
      </div>
      <p
        className={`mt-2 text-[10px] uppercase ${accent === 'danger' ? 'command-center-danger-text' : 'text-slate-500'}`}
      >
        {helper}
      </p>
    </div>
  </SurfaceCard>
);

const PresetCard = ({ icon: Icon, iconClassName, title, description, status }) => (
  <div className="dashboard-preset-shell group cursor-pointer rounded-md p-1">
    <div className="dashboard-preset-card h-full rounded-md p-6 transition-colors">
      <div
        className={`mb-6 flex h-12 w-12 items-center justify-center rounded-md ${iconClassName}`}
      >
        <Icon className="h-6 w-6" />
      </div>
      <h4 className="font-command-display text-lg font-bold text-white">{title}</h4>
      <p className="dashboard-preset-description mt-2 text-sm leading-6">{description}</p>
      <div className="dashboard-preset-status mt-6 flex items-center justify-between font-command-label text-[10px] uppercase tracking-[0.18em]">
        <span>{status}</span>
        <span>→</span>
      </div>
    </div>
  </div>
);

const EmptyState = ({ children }) => (
  <div className="dashboard-empty-state rounded-md border border-dashed p-8 text-center text-sm text-slate-400">
    {children}
  </div>
);

SurfaceCard.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string
};

SurfaceCard.defaultProps = {
  className: ''
};

MetricCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  helper: PropTypes.string.isRequired,
  accent: PropTypes.string
};

MetricCard.defaultProps = {
  accent: 'success'
};

PresetCard.propTypes = {
  icon: PropTypes.elementType.isRequired,
  iconClassName: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired
};

EmptyState.propTypes = {
  children: PropTypes.node.isRequired
};

const CommandCenter = () => {
  const { t } = useTranslation();
  const { data, updateFilterDate } = useWorkdaySummary();
  const isShowingToday = data.filterDate === data.currentDate;

  const workersByGroup = [...(data.workersByGroup || [])]
    .map(group => ({
      groupName: group.groupName || t('common.notAvailable'),
      workerCount: getCount(group.workerCount)
    }))
    .sort((left, right) => right.workerCount - left.workerCount);

  const workersInBlock = [...(data.workersInBlock || [])]
    .map(block => ({
      blockName: block.blockName || t('common.notAvailable'),
      workerCount: getCount(block.workerCount)
    }))
    .sort((left, right) => right.workerCount - left.workerCount);

  const farmActivityData = data.farmActivityData || [];
  const lastActivitiesData = data.lastActivitiesData || [];
  const activeBlocks = workersInBlock.filter(block => block.workerCount > 0).length;
  const selectedDate = formatDate(data.filterDate);
  const maxWorkersInBlock = Math.max(...workersInBlock.map(block => block.workerCount), 1);
  const leadGroup = workersByGroup[0];
  const efficiencyRate =
    data.totalWorkers > 0
      ? Math.min(99.9, ((activeBlocks + workersByGroup.length) / data.totalWorkers) * 100)
      : 0;
  const wasteRate = Math.max(0.8, 3.2 - workersByGroup.length * 0.35);
  const chartBlocks = workersInBlock.slice(0, 7);
  const topLeaders = workersByGroup.slice(0, 3);
  const laborShifts = workersByGroup.slice(0, 3).map((group, index) => {
    const target = 85;
    const performance =
      maxWorkersInBlock > 0 ? Math.round((group.workerCount / maxWorkersInBlock) * 100) : 0;
    return {
      label: `Shift ${String.fromCharCode(65 + index)}`,
      performance,
      delta: performance - target
    };
  });

  if (data.loading) {
    return (
      <div className="command-center-page px-4 py-8 sm:px-4">
        <div className="mx-auto max-w-[1600px] animate-pulse space-y-6">
          <div className="h-12 w-80 rounded-md bg-white/10" />
          <div className="grid gap-6 md:grid-cols-4">
            <div className="h-64 rounded-md bg-white/10 md:col-span-2" />
            <div className="h-64 rounded-md bg-white/10" />
            <div className="h-64 rounded-md bg-white/10" />
          </div>
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="h-96 rounded-md bg-white/10 lg:col-span-2" />
            <div className="h-96 rounded-md bg-white/10" />
          </div>
        </div>
      </div>
    );
  }

  if (data.error) {
    return (
      <div className="command-center-page px-4 py-8 sm:px-4">
        <div className="mx-auto max-w-[1600px]">
          <SurfaceCard className="p-8">
            <p className="command-center-danger-text font-command-label text-[11px] uppercase tracking-[0.24em]">
              {t('common.error')}
            </p>
            <h1 className="mt-3 font-command-display text-3xl font-bold text-white">
              {t('dashboard.analyticsTitle')}
            </h1>
            <p className="mt-4 text-sm text-slate-300">{data.error}</p>
          </SurfaceCard>
        </div>
      </div>
    );
  }

  return (
    <div className="command-center-page min-h-screen p-6 space-y-5">
      <SectionIntro
        kicker={t('dashboard.analyticsKicker')}
        title={t('dashboard.analyticsTitle')}
        kickerClassName="command-center-accent-text"
        actions={
          <>
            <div className="dashboard-chip flex items-center gap-2 rounded-md px-4 py-2 font-command-label text-[10px] uppercase tracking-[0.18em] text-slate-400">
              <CalendarDaysIcon className="command-center-accent-text h-4 w-4" />
              <span>{t('dashboard.snapshotMode')}</span>
            </div>

            <div className="dashboard-chip dashboard-chip--filled flex items-center gap-2 rounded-md px-4 py-2 font-command-label text-xs uppercase tracking-widest text-slate-200">
              <FunnelIcon className="h-4 w-4" />
              {selectedDate}
            </div>

            <button
              type="button"
              onClick={() => updateFilterDate(data.currentDate)}
              disabled={isShowingToday}
              className={`flex items-center gap-2 rounded-md border px-4 py-2 font-command-label text-xs uppercase tracking-widest transition-colors ${
                isShowingToday
                  ? 'workspace-button--disabled cursor-not-allowed'
                  : 'workspace-button hover:brightness-105'
              }`}
            >
              <ArrowTrendingUpIcon className="h-4 w-4" />
              {isShowingToday ? t('dashboard.showingToday') : t('dashboard.jumpToToday')}
            </button>
          </>
        }
      />

      <hr className="workspace-summary-divider my-5 border-t" />

      <div className="grid grid-cols-1 gap-5 md:grid-cols-4">
        <SurfaceCard className="relative overflow-hidden p-8 md:col-span-2">
          <div className="absolute right-0 top-0 p-8 opacity-10">
            <CalendarDaysIcon className="command-center-accent-text h-28 w-28" />
          </div>
          <p className="mb-6 font-command-label text-[10px] uppercase tracking-widest text-slate-400">
            {t('dashboard.aggregateCropYield')}
          </p>
          <div className="mb-2 flex items-baseline gap-4">
            <span className="font-command-display text-7xl font-extrabold tracking-tighter text-white">
              {data.totalWorkers}
            </span>
            <span className="font-command-display text-2xl font-bold text-slate-500">
              {t('dashboard.workersUpper')}
            </span>
          </div>
          <div className="command-center-accent-text flex items-center gap-2">
            <ArrowTrendingUpIcon className="h-4 w-4" />
            <span className="font-command-label text-sm font-bold">
              {leadGroup
                ? `${leadGroup.groupName} · ${leadGroup.workerCount} ${t('dashboard.workers').toLowerCase()}`
                : t('dashboard.noGroupData')}
            </span>
          </div>
        </SurfaceCard>

        <MetricCard
          label={t('dashboard.operationalEfficiency')}
          value={`${efficiencyRate.toFixed(1)}%`}
          helper={`${t('dashboard.targetLabel')}: 92.0%`}
        />

        <MetricCard
          label={t('dashboard.wasteRate')}
          value={`${wasteRate.toFixed(1)}%`}
          helper={t('dashboard.wasteRateWarning')}
          accent="danger"
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <SurfaceCard className="p-8 lg:col-span-2">
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h2 className="font-command-display text-xl font-bold text-white">
                {t('dashboard.yieldDistribution')}
              </h2>
              <p className="text-sm text-slate-500">{t('dashboard.yieldDistributionSubtitle')}</p>
            </div>
            <EllipsisVerticalIcon className="h-5 w-5 text-slate-500" />
          </div>

          <div className="flex h-64 items-end justify-between gap-3 px-4">
            {chartBlocks.length > 0 ? (
              chartBlocks.map(block => {
                const outerHeight = Math.max(
                  28,
                  Math.round((block.workerCount / maxWorkersInBlock) * 100)
                );
                const innerHeight = Math.max(24, outerHeight - 18);

                return (
                  <div key={block.blockName} className="relative h-full w-full">
                    <div className="group absolute bottom-0 flex h-full w-full flex-col justify-end">
                      <div
                        className="command-center-bar-shell relative rounded-t-lg"
                        style={{ height: `${outerHeight}%` }}
                      >
                        <div
                          className="command-center-bar-fill absolute bottom-0 w-full rounded-t-lg transition-all group-hover:h-full"
                          style={{ height: `${innerHeight}%` }}
                        />
                      </div>
                      <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 font-command-label text-[9px] text-slate-500">
                        {block.blockName}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="w-full py-16">
                <EmptyState>{t('dashboard.noBlockData')}</EmptyState>
              </div>
            )}
          </div>
        </SurfaceCard>

        <SurfaceCard className="flex flex-col p-8">
          <div className="mb-6">
            <h2 className="font-command-display text-xl font-bold text-white">
              {t('dashboard.efficiencyLeaders')}
            </h2>
            <p className="text-sm text-slate-500">{t('dashboard.efficiencyLeadersSubtitle')}</p>
          </div>
          <div className="flex-1 space-y-6">
            {topLeaders.length > 0 ? (
              topLeaders.map((group, index) => {
                const performance =
                  maxWorkersInBlock > 0
                    ? Math.round((group.workerCount / maxWorkersInBlock) * 124)
                    : 0;
                const delta = index === 2 ? '-1%' : `+${Math.max(1, 4 - index * 2)}%`;

                return (
                  <div key={group.groupName} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold ${
                          index === 0
                            ? 'command-center-rank command-center-rank--leader'
                            : 'command-center-rank'
                        }`}
                      >
                        {String(index + 1).padStart(2, '0')}
                      </div>
                      <div>
                        <p className="text-sm font-bold text-white">{group.groupName}</p>
                        <p className="font-command-label text-[10px] text-slate-500">
                          {t('dashboard.workersAssigned')}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p
                        className={`text-sm font-bold ${index === 0 ? 'command-center-accent-text' : 'text-white'}`}
                      >
                        {performance}kg/h
                      </p>
                      <p className="text-[10px] text-slate-500">{delta}</p>
                    </div>
                  </div>
                );
              })
            ) : (
              <EmptyState>{t('dashboard.noGroupData')}</EmptyState>
            )}
          </div>
          <button className="dashboard-view-all mt-8 w-full text-center font-command-label text-xs uppercase tracking-widest transition-colors">
            {t('dashboard.viewAllPersonnel')}
          </button>
        </SurfaceCard>
      </div>

      <div>
        <h2 className="mb-6 font-command-display text-2xl font-bold text-white">
          {t('dashboard.reportPresets')}
        </h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <PresetCard
            icon={DocumentTextIcon}
            iconClassName="dashboard-preset-icon dashboard-preset-icon--harvest"
            title={t('dashboard.presetMonthlyHarvest')}
            description={t('dashboard.presetMonthlyHarvestDescription')}
            status={t('dashboard.presetMonthlyHarvestStatus')}
          />
          <PresetCard
            icon={ChartBarIcon}
            iconClassName="dashboard-preset-icon dashboard-preset-icon--health"
            title={t('dashboard.presetBlockHealth')}
            description={t('dashboard.presetBlockHealthDescription')}
            status={t('dashboard.presetBlockHealthStatus')}
          />
          <PresetCard
            icon={InboxStackIcon}
            iconClassName="dashboard-preset-icon dashboard-preset-icon--allocation"
            title={t('dashboard.presetResourceAllocation')}
            description={t('dashboard.presetResourceAllocationDescription')}
            status={t('dashboard.presetResourceAllocationStatus')}
          />
        </div>
      </div>

      <SurfaceCard className="command-center-panel p-8">
        <div className="mb-8">
          <h2 className="font-command-display text-xl font-bold text-white">
            {t('dashboard.laborEfficiencyTarget')}
          </h2>
          <p className="text-sm text-slate-500">{t('dashboard.laborEfficiencyTargetSubtitle')}</p>
        </div>

        <div className="space-y-4">
          {laborShifts.length > 0 ? (
            laborShifts.map(shift => {
              const isPositive = shift.delta >= 0;
              return (
                <div
                  key={shift.label}
                  className="grid grid-cols-[100px_1fr_60px] items-center gap-4"
                >
                  <span className="font-command-label text-xs text-slate-400">
                    {shift.label.toUpperCase()}
                  </span>
                  <div className="command-center-progress-track relative h-8 overflow-hidden rounded-lg">
                    <div
                      className={`absolute left-0 top-0 h-full border-r ${isPositive ? 'command-center-progress-fill command-center-progress-fill--positive' : 'command-center-progress-fill command-center-progress-fill--negative'}`}
                      style={{ width: `${Math.max(12, Math.min(100, shift.performance))}%` }}
                    />
                    <div className="command-center-progress-baseline absolute left-[85%] top-0 h-full w-[2px]" />
                  </div>
                  <span
                    className={`text-xs font-bold ${isPositive ? 'command-center-accent-text' : 'command-center-danger-text'}`}
                  >
                    {isPositive ? `+${shift.delta}%` : `${shift.delta}%`}
                  </span>
                </div>
              );
            })
          ) : (
            <EmptyState>{t('dashboard.noGroupData')}</EmptyState>
          )}
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-6 font-command-label text-[10px] uppercase text-slate-500">
          <div className="flex items-center gap-2">
            <div className="command-center-progress-legend h-3 w-3" />
            {t('dashboard.actualPerformance')}
          </div>
          <div className="flex items-center gap-2">
            <div className="command-center-progress-baseline h-3 w-[2px]" />
            {t('dashboard.baselineTarget')}
          </div>
        </div>
      </SurfaceCard>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <SurfaceCard className="p-8">
          <div>
            <h2 className="font-command-display text-xl font-bold text-white">
              {t('dashboard.farmActivitySignal')}
            </h2>
            <p className="text-sm text-slate-500">{t('dashboard.farmActivitySignalSubtitle')}</p>
          </div>

          <div className="mt-8 space-y-3">
            {farmActivityData.length > 0 ? (
              farmActivityData.slice(0, 4).map(activity => (
                <div
                  key={`${activity.activityName}-${activity.blockName}-${activity.firstScan}`}
                  className="command-center-row grid gap-4 rounded-md border p-4 md:grid-cols-[minmax(0,1.4fr)_110px_110px_140px]"
                >
                  <div className="min-w-0">
                    <p className="font-command-display text-lg font-bold text-white">
                      {activity.activityName || t('common.notAvailable')}
                    </p>
                    <p className="mt-1 text-sm text-slate-400">
                      {[activity.cropName, activity.varietyName].filter(Boolean).join(' / ') ||
                        t('common.notAvailable')}
                    </p>
                    <p className="mt-3 font-command-label text-[11px] uppercase tracking-[0.24em] text-slate-500">
                      {[activity.blockName, activity.groupName].filter(Boolean).join(' / ')}
                    </p>
                  </div>
                  <div>
                    <p className="font-command-label text-[11px] uppercase tracking-[0.24em] text-slate-500">
                      {t('dashboard.plantsTracked')}
                    </p>
                    <p className="mt-3 text-sm font-semibold text-white">
                      {getCount(activity.numberOfPlants)}
                    </p>
                  </div>
                  <div>
                    <p className="font-command-label text-[11px] uppercase tracking-[0.24em] text-slate-500">
                      {t('dashboard.workersAssigned')}
                    </p>
                    <p className="mt-3 text-sm font-semibold text-white">
                      {getCount(activity.workerCount)}
                    </p>
                  </div>
                  <div>
                    <p className="font-command-label text-[11px] uppercase tracking-[0.24em] text-slate-500">
                      {t('dashboard.scanWindow')}
                    </p>
                    <p className="mt-3 text-sm font-semibold text-white">
                      {formatTime(activity.firstScan, t('common.notAvailable'))} -{' '}
                      {formatTime(activity.lastScan, t('common.notAvailable'))}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <EmptyState>{t('dashboard.noActivityData')}</EmptyState>
            )}
          </div>
        </SurfaceCard>

        <SurfaceCard className="p-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="font-command-display text-xl font-bold text-white">
                {t('dashboard.recentMovements')}
              </h2>
              <p className="text-sm text-slate-500">{t('dashboard.recentMovementsSubtitle')}</p>
            </div>
            <div className="command-center-icon-badge rounded-md border p-3">
              <ClockIcon className="h-5 w-5" />
            </div>
          </div>

          <div className="mt-8 space-y-3">
            {lastActivitiesData.length > 0 ? (
              lastActivitiesData.slice(0, 6).map(activity => (
                <div
                  key={`${activity.workerId}-${activity.lastActivity}-${activity.timePassed}`}
                  className="command-center-row rounded-md border p-4"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-command-display text-lg font-bold text-white">
                        {activity.workerName || activity.workerId}
                      </p>
                      <p className="mt-1 text-sm text-slate-400">
                        {activity.lastActivity || t('common.notAvailable')}
                      </p>
                    </div>
                    <span className="command-center-pill rounded-full border px-3 py-1 text-xs text-slate-300">
                      {activity.timePassed || t('common.notAvailable')}
                    </span>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2 text-xs text-slate-400">
                    <span className="command-center-pill inline-flex items-center gap-2 rounded-full border px-3 py-1">
                      <Squares2X2Icon className="h-3.5 w-3.5" />
                      {activity.blockName || t('common.notAvailable')}
                    </span>
                    <span className="command-center-pill inline-flex items-center gap-2 rounded-full border px-3 py-1">
                      <MapPinIcon className="h-3.5 w-3.5" />
                      {activity.location
                        ? activity.location.split(',').slice(0, 2).join(' / ')
                        : t('common.notAvailable')}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <EmptyState>{t('dashboard.noRecentMovement')}</EmptyState>
            )}
          </div>
        </SurfaceCard>
      </div>
    </div>
  );
};

export default CommandCenter;
